import { formatISO } from 'date-fns';
import { ConcussionAnalysis } from 'features/patient/_utils/patient-info/patient-info-slice-types';
import {
  CONCUSSION_COMPLEXITY_COLORS,
  pluraliseConcussionComplexity
} from 'features/patient/patient-home/components/patient-home-clinician/components/styled-concussion-complexity';
import {
  API_TO_COMPLEXITY_MAP,
  COMPLEXITY_ORDER
} from 'lib/documents/concussion-complexity';
import { PlotParams } from 'react-plotly.js';
import {
  computeTotalScore,
  NScoreWindow
} from '../display-total-symptom-tracker';
import { CgsInventory } from 'features/patient/assessment/questionnaires/cgs/cgsSlice';

const GRAPH_HEIGHT = 400;

/**
 * Generate a Plotly Plot config for a Concussion Complexity Progression visualisation
 * @param concussionData
 * @param width
 * @returns
 */
export function getPlotlyConfigForConcussionComplexityProgression(
  concussionData: ConcussionAnalysis[],
  width: number
): Pick<PlotParams, 'data' | 'layout' | 'config'> {
  const enumeratedKeys = Object.keys(API_TO_COMPLEXITY_MAP)
    .map(_ => parseInt(_))
    .sort();
  const min = enumeratedKeys.at(0);
  const max = enumeratedKeys.at(-1);

  const x =
    concussionData.map(_ =>
      formatISO(_.date.toDate(), { representation: 'date' })
    ) ?? [];
  const y =
    concussionData.map(_ => {
      const subtypeFromAPI = _.data.subtype;
      const name = API_TO_COMPLEXITY_MAP[subtypeFromAPI];
      const ordinalValue = COMPLEXITY_ORDER.indexOf(name);
      if (ordinalValue === undefined) {
        throw new Error(
          `[DisplayConcussionComplexityProgression] Complexity "${name}" was not found. Available values: ${JSON.stringify(
            COMPLEXITY_ORDER
          )}`
        );
      }

      return ordinalValue;
    }) ?? [];
  const barChartColors = y.map(val => {
    const complexityName = COMPLEXITY_ORDER[val];
    if (complexityName === undefined) {
      throw new Error(
        `[DisplayConcussionComplexityProgression] Complexity at index ${val} was not found. Length: ${COMPLEXITY_ORDER.length}`
      );
    }

    const complexityColor = CONCUSSION_COMPLEXITY_COLORS[complexityName];
    if (complexityColor === undefined) {
      throw new Error(
        `[DisplayConcussionComplexityProgression] Complexity color for name '${complexityName}' was not found`
      );
    }

    return complexityColor.baseColour;
  });

  return {
    data: [
      {
        type: 'bar',
        x,
        // Increment y by 1 so that minimally complex (0) is set to 1
        y: y.map(_ => _ + 1),
        marker: {
          color: barChartColors
        },
        width: 0.1
      }
    ],
    layout: {
      autosize: true,
      height: GRAPH_HEIGHT,
      width: width,

      xaxis: {
        type: 'category',
        title: {
          text: 'Date'
        }
      },
      margin: {
        r: 0,
        /**
         * Pad the graph so that the y-axis title and y-axis labels do not overlap.
         * This value was manually selected
         */
        l: 95
      },
      /**
       * We are not including a title because this graph is displayed in a parent component that shows the same title
       */
      // title: {
      //   text: "Concussion Complexity Over Time"
      // },
      yaxis: {
        title: {
          text: 'Concussion Complexity'
        },
        ticktext: COMPLEXITY_ORDER.map(_ => pluraliseConcussionComplexity(_)),
        // Offset the range and ticks by 1 so that minimally complex has a height of 1
        tickvals: COMPLEXITY_ORDER.map((_, i) => i + 1),
        range: [min, max! + 1]
      }
    },
    config: {
      displayModeBar: false,
      responsive: true
    }
  };
}
/**
 * Generate a Plotly Plot configuration for the Total Symptom Tracker visualisation
 * @param cgsData
 * @param width
 * @returns
 */

export function getPlotlyConfigForTotalSymptomTracker(
  cgsData: CgsInventory[],
  width: number
): Pick<PlotParams, 'data' | 'layout' | 'config'> {
  const xValues = cgsData.map(_ =>
    formatISO(_.date.toDate(), { representation: 'date' })
  );
  const yValues = cgsData.map(_ => computeTotalScore(_.data));

  /**
   * Pick the top n scores from the first assessment
   */
  const topNScores = Object.entries(cgsData.at(0)?.data ?? {})
    .sort((a, b) => a[0].localeCompare(b[0]))
    .sort((a, b) => b[1] - a[1])
    .slice(0, NScoreWindow);

  return {
    data: [
      {
        type: 'scatter',
        x: xValues,
        y: yValues,
        mode: 'lines+markers',
        line: {
          color: 'royalblue',
          width: 3
        },
        marker: {
          size: 10
        }
      }
    ],
    layout: {
      height: GRAPH_HEIGHT,
      width: width,
      title: {
        text: `Top 3 symptoms`,
        subtitle: {
          text: topNScores.map(_ => _[0]).join(', ')
        }
      },
      xaxis: {
        type: 'category',
        title: {
          text: 'Date'
        }
      },
      yaxis: {
        title: {
          text: 'Total Score'
        },
        range: [0, 100]
      },
      legend: {
        orientation: 'h',
        yanchor: 'bottom',
        y: 1.02,
        xanchor: 'right',
        x: 1
      }
    },
    config: {
      displayModeBar: false,
      responsive: true
    }
  };
}
